// Packages
import React, { useEffect, useState, useRef } from 'react';
import Calendar from 'react-calendar';
import { Typography, Box, Alert, Modal } from '@mui/material';

// Local
import './CalendarTile.css';
import CalendarEventModal from '../../modals/CalendarEventModal';
import DateInformationModal from '../../modals/DateInformationModal';
import getExamDates from '../../helpers/getExamDates';
import withThisUser from '../../../wrappers/withThisUser';

const CalendarTile = ({thisUserData, setAlert=null, onRowClick=null}) => {
  /*
    CalendarTile React Component
    - styles the calendar using a local .css sheet
    - fetches and displays exam dates using the backend
  */

  //////////////////////////////////////////////////////////////////////////////////////////////////
  // DEFINITIONS
  //////////////////////////////////////////////////////////////////////////////////////////////////
  const [isReserveModalOpen, setReserveModalOpen] = useState(false);
  const openReserveModal = () => setReserveModalOpen(true);
  const closeReserveModal = () => setReserveModalOpen(false);

  const [isInfoModalOpen, setInfoModalOpen] = useState(false);
  const openInfoModal = () => setInfoModalOpen(true);
  const closeInfoModal = () => setInfoModalOpen(false);

  const [selectedCalendarDate, setSelectedCalendarDate] = useState(new Date());
  const [wasReservationSuccessful, setReservationSuccessful] = useState(false);
  const [selectedDateString, setSelectedDateString] = useState(null);
  const [clickedDateString, setClickedDateString] = useState(null);
  const [formattedSuccessDate, setFormattedSuccessDate] = useState("");

  const [allExamDates, setAllExamDates] = useState([]);
  const [examDataForClickedDate, setExamDataForClickedDate] = useState(null);

  const hasFetchedInitialExamDates = useRef(false);
  const [refreshCalendar, setRefreshCalendar] = useState(false);
  const handleRefreshCalendar = () => setRefreshCalendar(!refreshCalendar);

  //////////////////////////////////////////////////////////////////////////////////////////////////
  // ON LOAD
  //////////////////////////////////////////////////////////////////////////////////////////////////

  useEffect(() => {
    getExamDates('all', (data) => {
      setAllExamDates(data);
      hasFetchedInitialExamDates.current = true;
    });
  }, [refreshCalendar]);

  //////////////////////////////////////////////////////////////////////////////////////////////////
  // STYLES
  //////////////////////////////////////////////////////////////////////////////////////////////////

  const containerStyles = {
    display: 'flex',
    flexDirection: 'column',
    boxShadow: 3,
    borderRadius: 4,
    height: 'auto',
    width: '100%',
  };
  
  const testerContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    boxShadow: 3,
    borderRadius: 4,
    height: 'auto',
    width: '100%',
  }

  const headerBoxStyles = {
    bgcolor: '#1483FA',
    color: '#FFFFFF',
    px: 2,
    pt: 2,
    borderTopRightRadius: 16,
    borderTopLeftRadius: 16,
    pb: 1,
  };

  const headerTextStyles = {
    fontWeight: 650,
    textShadow: '2px 2px 4px rgba(0, 0, 0, 0.25)',
  };

  const calendarBoxStyles = { p: 2 };

  //////////////////////////////////////////////////////////////////////////////////////////////////
  // RENDER
  //////////////////////////////////////////////////////////////////////////////////////////////////

  return (
    <Box sx={thisUserData.userType ==='district_admin' ? containerStyles : testerContainerStyles}>
      {thisUserData.userType === 'district_admin' && (
        <Modal open={isReserveModalOpen} onClose={closeReserveModal}>
          <CalendarEventModal
            date={selectedDateString}
            handleClose={closeReserveModal}
            setSuccess={setReservationSuccessful}
            setDisplayDate={setFormattedSuccessDate}
            refreshHook={handleRefreshCalendar}
          />
        </Modal>
      )}

      <Modal open={isInfoModalOpen} onClose={closeInfoModal}>
        <DateInformationModal
          date={clickedDateString}
          examDateInfo={examDataForClickedDate}
          handleClose={closeInfoModal}
          userData={thisUserData}
          refreshHook={handleRefreshCalendar}
          setAlert={setAlert}
          onRowClick={onRowClick}
        />
      </Modal>
        
   
      <Box sx={headerBoxStyles}>
        <Typography variant="h4" sx={headerTextStyles}>Schedule</Typography>
      </Box>

      <Box sx={calendarBoxStyles}>
        <Calendar
          onChange={setSelectedCalendarDate}
          value={selectedCalendarDate}
          onClickDay={(dateObj) => {
            const dateStr = new Date(dateObj).toISOString();
            setSelectedDateString(dateStr);
            setClickedDateString(dateStr);

            if (!hasFetchedInitialExamDates.current) return;

            getExamDates(dateStr, (data) => {
              setExamDataForClickedDate(data);
              if (data !== null) {
                openInfoModal();
              } else {
                openReserveModal();
              }
            });
          }}
          tileClassName={({ date, view }) => {
            if (view === 'month' && allExamDates?.exam_dates?.length > 0) {
              const dateStr = new Date(date).toISOString();
              return allExamDates.exam_dates.some(
                (exam) => exam.date.split('T')[0] === dateStr.split('T')[0]
              )
                ? 'highlight'
                : null;
            }
          }}
        />

        {wasReservationSuccessful && (
          <Alert
            severity="success"
            sx={{ mt: 2 }}
            onClose={() => setReservationSuccessful(false)}
          >
            {formattedSuccessDate} has been reserved.
          </Alert>
        )}
      </Box>
    </Box>
  );
};

export default withThisUser(CalendarTile);
